import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalSize,
    NotificationToaster,
} from "components/notifications";
import PropTypes from "prop-types";
import { Button, ButtonSave } from "components/buttons";
import { FormRadio, FormTextInput } from "components/form-fields";
import { LicenceCheckingService } from "services";
import { ShowHide } from "components/layout";
import { Intent } from "@blueprintjs/core";
import { useValidation } from "hooks/useValidation";

export function LicenceCheckingNewConsentModal({
    isOpen,
    onClose,
    onSuccess,
    employeeName = "",
    employeeId = "",
    emailAddress = "",
    consentExists = false
}) {
    const validationErrorResponse = 400;
    const [consentRequest, setConsentRequest] = useState({
        employeeId: null,
        emailAddress: "",
        updateEmployeeEmail: false,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isValid, errors, validate] = useValidation();

    function onConfirm() {
        setIsSaving(true);
        LicenceCheckingService.sendDriverDeclaration(consentRequest)
            .then(
                function () {
                    onSuccess();
                    onClose();
                },
                (error) => {
                    if (error.response.status === validationErrorResponse) {
                        NotificationToaster.show(
                            Intent.WARNING,
                            error.response.data
                        );
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Failed to send declaration. Please try again."
                        );
                    }
                }
            )
            .finally(function () {
                setIsSaving(false);
            });
    }

    function onEmailChange(event) {
        const newEmail = event.target.value;
        setConsentRequest(function (prev) {
            return {
                ...prev,
                emailAddress: newEmail,
            };
        });
    }

    function onUpdateProfileToggle(event) {
        const update = event.target.value;
        setConsentRequest(function (prev) {
            return {
                ...prev,
                updateEmployeeEmail: update === "true",
            };
        });
    }

    useEffect(() => {
        setConsentRequest(function (prev) {
            return {
                ...prev,
                emailAddress: emailAddress,
                employeeId: employeeId,
            };
        });
    }, [employeeId, emailAddress]);

    useEffect(() => {
        const rules = [
            { fieldName: "emailAddress", required: true, type: "email" },
        ];
        validate(rules, consentRequest);
    }, [consentRequest.emailAddress]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                !consentExists
                    ? "New Driver Declaration"
                    : "Resend Driver Declaration"
            }
            size={ModalSize.SMALL}
            clickOutsideClose={false}
            showCloseButton={false}
        >
            <FormTextInput
                headingText={`Email Address for ${employeeName}`}
                disabled={isSaving}
                wide
                onChange={onEmailChange}
                value={consentRequest.emailAddress}
                maxLength={255}
                dangerHelperText={errors.emailAddress}
            />
            <ShowHide
                evaluator={consentRequest.emailAddress !== emailAddress}
                show={
                    <FormRadio
                        headingText="Would you like to update the employee profile with this email address?"
                        selectedValue={consentRequest.updateEmployeeEmail}
                        onChange={onUpdateProfileToggle}
                        disabled={false}
                    />
                }
            />
            <div className="inline-items inline-items-end">
                <Button
                    text="Cancel"
                    intent="Secondary"
                    onClick={onClose}
                    disabled={isSaving}
                />
                <ButtonSave
                    simpleDisabled={!isValid}
                    disabled={isSaving}
                    icon={null}
                    text="Send Declaration"
                    onClick={onConfirm}
                />
            </div>
        </Modal>
    );
}

LicenceCheckingNewConsentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
    emailAddress: PropTypes.string,
    consentExists: PropTypes.bool,
};
